import API_BASE from "../../../config";

/* ── Auth headers ────────────────────────────────────────────────────────
   Mirrors the pattern used throughout the rest of the app.
   Reads from sessionStorage first (session-scoped), then localStorage
   (remember-me persistence). Both paths are guarded with try/catch so a
   corrupted value never breaks the UI. */
export function authHeaders() {
  let user = null;
  try {
    user = JSON.parse(
      sessionStorage.getItem("user") || localStorage.getItem("user") || "null"
    );
  } catch { /* ignore */ }
  return {
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
    ...(user?.linkedEntityId != null
      ? { "X-Linked-Entity-Id": String(user.linkedEntityId) } : {}),
    ...(user?.linkedEntityType
      ? { "X-Linked-Entity-Type": user.linkedEntityType } : {}),
  };
}

export function getUserRole() {
  let user = null;
  try {
    user = JSON.parse(
      sessionStorage.getItem("user") || localStorage.getItem("user") || "null"
    );
  } catch { /* ignore */ }
  return user?.role || "";
}

/* ── Role gates ──────────────────────────────────────────────────────────
   canSeeMasterList  — controls whether the "Master List" tab appears in
                       DirectoryPage at all.
   canSeeTab(key)    — controls which inner tabs are visible per-role once
                       the modal is open.

   These gates are client-side visibility helpers only. The server enforces
   its own auth on every request; a role mismatch there returns 403 and the
   tab shows an appropriate error state rather than exposing data. */

export function canSeeMasterList() {
  const r = getUserRole();
  return (
    r === "SuperDev" ||
    r === "Owner" || r === "SuperAdmin" || r === "Admin" ||
    r === "Registrar" || r === "RegistrarStaff" ||
    r === "Principal" || r === "AcademicHead"
  );
}

export function canEditStudentProfile() {
  const r = getUserRole();
  return (
    r === "SuperDev" || r === "Owner" || r === "SuperAdmin" || r === "Admin" ||
    r === "Registrar"
  );
}

/** Who may move a student to another grade / section. Must match the
 *  backend AdminGuard list, which guards PUT /api/enrollments/{id}. */
export function canChangePlacement() {
  const r = getUserRole();
  return (
    r === "SuperDev" || r === "Owner" || r === "SuperAdmin" || r === "Admin" ||
    r === "Registrar" || r === "RegistrarStaff"
  );
}

export function canEditParent() {
  const r = getUserRole();
  return (
    r === "SuperDev" || r === "Owner" || r === "SuperAdmin" || r === "Admin" ||
    r === "Registrar"
  );
}

export function canSeeTab(tabKey) {
  const r = getUserRole();
  const admin    = r === "SuperDev" || r === "Owner" || r === "SuperAdmin" || r === "Admin";
  const registry = r === "Registrar" || r === "RegistrarStaff";
  const academic = r === "Principal" || r === "AcademicHead";
  const teacher  = r === "Teacher";
  const cashier  = r === "Cashier" || r === "CashierStaff";
  const clinic   = r === "Nurse" || r === "ClinicStaff";

  switch (tabKey) {
    case "profile":    return admin || registry || academic;
    case "family":     return admin || registry || academic || clinic;
    case "academic":   return admin || registry || academic || teacher;
    case "grades":     return admin || registry || academic || teacher;
    case "attendance": return admin || registry || academic || teacher;
    case "finances":   return admin || registry || cashier;
    case "health":     return admin || registry || clinic;
    case "books":      return admin || registry || cashier;
    case "documents":  return admin || registry;
    case "sports":     return admin || registry || academic || teacher;
    default:           return false;
  }
}

/* ── Avatar helpers ──────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  "#5a4e3a", "#3a4e4a", "#4a3a5a", "#4e4a3a",
  "#3a4a5a", "#5a3a3a", "#3a5a3a",
];
export function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++)
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
export function initials(first, last) {
  return ((last?.[0] ?? "") + (first?.[0] ?? "")).toUpperCase() || "?";
}
export function photoSrc(photoUrl) {
  if (!photoUrl) return null;
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${API_BASE}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
}

/* ── Formatters ──────────────────────────────────────────────────────── */
export function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
}

export function fmtDateShort(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export function fmtCurrency(amount) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  return "₱" + n.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fmtBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function calcAge(birthdate) {
  if (!birthdate) return null;
  const b = new Date(birthdate);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
}

/* ── Document helpers (reused in DocumentsTab) ───────────────────────── */
export const DOC_MAX_BYTES = 10 * 1024 * 1024;
export const DOC_ALLOWED_EXT = [
  ".pdf", ".jpg", ".jpeg", ".png", ".gif", ".webp",
  ".doc", ".docx", ".xls", ".xlsx",
];
export const DOC_ALLOWED_MIME = new Set([
  "application/pdf",
  "image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

export function validateDocFile(file) {
  if (!file) return "Pick a file to upload.";
  if (file.size > DOC_MAX_BYTES) {
    return `File is ${(file.size / 1024 / 1024).toFixed(1)} MB — limit is 10 MB.`;
  }
  const name = (file.name || "").toLowerCase();
  const okExt  = DOC_ALLOWED_EXT.some(ext => name.endsWith(ext));
  const okMime = !file.type || DOC_ALLOWED_MIME.has(file.type);
  if (!okExt || !okMime)
    return "Unsupported file type. Accepted: PDF, JPG, PNG, GIF, WEBP, DOC(X), XLS(X).";
  return null;
}

export function docIcon(contentType, filename) {
  const ct   = (contentType || "").toLowerCase();
  const name = (filename || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(name)) return "🖼️";
  if (ct === "application/pdf" || name.endsWith(".pdf"))                 return "📄";
  if (ct.includes("word")      || /\.docx?$/.test(name))                return "📝";
  if (ct.includes("excel") || ct.includes("spreadsheet") || /\.xlsx?$/.test(name)) return "📊";
  return "📎";
}
